// ============================================================
// src/pages/about.js
// Public-facing About page for Performer Pocket Planner.
// ============================================================

import { Link } from 'react-router-dom'

const APP_VERSION  = '0.3.0-beta'
const APP_NAME     = 'Performer Pocket Planner'
const CONTACT_SITE = 'https://ben-perk.com'
const CONTACT_LABEL = 'ben-perk.com'

const FEATURES = [
  {
    icon: 'bi-calendar-event-fill',
    title: 'Gig Management',
    description: 'Add, edit, and track every booking. Store the venue, booking fee, and show time all in one place.',
  },
  {
    icon: 'bi-music-note-list',
    title: 'Setlist Planner',
    description: 'Pin songs to a gig, arrange your running order, and LEARN YOUR WORDS. Includes shuffle, repeat (off / all / one), and a queue position indicator.',
  },
  {
    icon: 'bi-cash-coin',
    title: 'Earnings Tracker',
    description: 'Log booking fees and tips. See your monthly and all-time totals at a glance. Filter by year, month, venue, city, and booking type. You can even export to Excel.',
  },
  {
    icon: 'bi-calendar3',
    title: 'Calendar View',
    description: 'See all your upcoming gigs in a monthly calendar. Tap any date to preview the gig, check your drive time, and export the event to Apple or Google Calendar to have it on your phone.',
  },
  {
    icon: 'bi-bell-fill',
    title: 'Smart Notifications',
    description: 'Get up to six push notifications before every show. The app works out your drive time using live traffic, then sends reminders for getting ready, leaving home, and heading to the venue.',
  },
  {
    icon: 'bi-map-fill',
    title: 'Route Map',
    description: "See a live driving route from your home (or your hotel if you're on a trip) straight to the venue, with estimated drive time and distance on the gig detail page.",
  },
  {
    icon: 'bi-suitcase-fill',
    title: 'Trip Planner',
    description: "Traveling for a gig? While you're away, the app routes timing from your hotel instead of home.",
  },
  {
    icon: 'bi-images',
    title: 'Promo Photos',
    description: "Store your promo shots so they're always ready to send to promoters. Organize by category (headshots, waist up, full body, performance), tag them for quick searching, and edit details any time.",
  },
  {
    icon: 'bi-image',
    title: 'Show Promo Generator',
    description: 'Build a custom 1080x1080 Square monthly gig poster and download it as a PNG.',
  },
  {
    icon: 'bi-person-lines-fill',
    title: 'Contact Book',
    description: 'Keep bookers, venues, and collaborators in one searchable place.',
  },
  {
    icon: 'bi-bag-check-fill',
    title: 'Travel Checklist',
    description: 'A packing list you can check off before heading out for a gig. Save a standard list so you can reload your usual items.',
  },
  {
    icon: 'bi-car-front-fill',
    title: 'Pickup Time',
    description: 'Getting picked up instead of driving yourself? Log your pickup time and the today card calculates your get-ready start time from it. A warning comes up if your pickup arrives too late.',
  },
  {
    icon: 'bi-signpost-split-fill',
    title: 'Venue-to-Venue Routing',
    description: 'Have back-to-back shows? The today card asks if you are heading straight from the last venue or going home first. Choose straight there and it calculates a leave time from your current venue to the next one.',
  },
  {
    icon: 'bi-bag-fill',
    title: 'Errand Routing',
    description: 'Need to pick up lashes on the way to a show? Add an errand address and how long you will be there. The app routes you home to errand to venue and calculates when to leave for each stop.',
  },
  {
    icon: 'bi-calendar-music',
    title: 'Gig Playlist',
    description: 'In the Music Library, pick a time window (this week, next two weeks, or next 30 days) to build a queue from every setlist pinned to your upcoming gigs. Hit Play All to rehearse your full run, and LEARN YOUR WORDS.',
  },
 
  {
    icon: 'bi-stars',
    title: 'Getting Ready Planner',
    description: 'When saving a gig, tell the app where you are getting ready (home, another location, or at the venue) and how much prep happens before you leave under settings. The today card splits your prep time correctly and shows when to start.',
  },
  {
    icon: 'bi-briefcase-fill',
    title: 'Managed Gigs',
    description: "Booked through an agent or manager? Mark a gig as managed and enter the fee as a percentage or flat amount. The app shows your take-home pay everywhere the gig appears.",
  },
  {
    icon: 'bi-bell',
    title: 'Booking Requests',
    description: "Producers using the Producer Pocket Planner can send you show requests directly. Accept, decline, or review details from the Requests inbox. Conflict and trip warnings fire automatically.",
  },
  {
    icon: 'bi-link-45deg',
    title: 'Fan Links Page',
    description: "A public page with your upcoming shows, social handles, website, and digital tip buttons. Customize the colors and fonts, then share the link as your bio link on any platform, so your fans can support you!",
  },
  {
    icon: 'bi-palette-fill',
    title: 'Themes',
    description: "Switch the entire app color palette and header font instantly from Settings, there are 4 themes to choose from: It's Pink!, Green Gush, Bone, and Blue Shock.",
  },
]


function About() {
  return (
    <div className="about-page">

      {/* HERO */}
      {/* .about-hero: pink-to-purple gradient, white text, centred */}
      <div className="about-hero">
        <div className="about-hero-logo">
          <img src="/image/logo.png" alt="" />
        </div>

        <h1>{APP_NAME}</h1>

        <p>Gig management built for performers, by a performer!</p>

        {/* .about-version-badge: semi-transparent pill with uppercase text */}
        <span className="about-version-badge">
          Beta v{APP_VERSION}
        </span>
      </div>


      {/* WHAT IS THIS? */}
      <section className="about-section">
        <h2>What is this?</h2>
        <p className="about-prose">
          <strong>{APP_NAME}</strong> is a web app for performing artists. This was made to help keep everything in one place, and help out performers.
        </p>
        <p className="about-prose">
          It is in <strong>beta</strong> right now, which means it works but is still being improved.
          Your feedback shapes what gets built next.
        </p>
      </section>


      {/* FEATURE GRID */}
      <section className="about-section">
        <h2>What can it do?</h2>

        {/* .about-feature-grid: 2-col auto-fill grid */}
        <div className="about-feature-grid">
          {FEATURES.map(feature => (
            <div key={feature.title} className="about-feature-card">
              {/* .about-feature-icon: 40×40 pink-pale circle with the icon */}
              <div className="about-feature-icon">
                <i className={`bi ${feature.icon}`}></i>
              </div>
              <div>
                <div className="about-feature-title">{feature.title}</div>
                <div className="about-feature-desc">{feature.description}</div>
              </div>
            </div>
          ))}
        </div>
      </section>


      {/* NOTIFICATIONS INFO */}
      {/* .about-info-section: white card, accent border */}
      <div className="about-info-section">
        <h2>How notifications work</h2>
        <h3>Notifications are still a work in progress and not fully operational.</h3>
        <p className="about-prose">
          When you save a gig, the app calculates your drive time using <strong>Mapbox</strong> live
          traffic information, then schedules up to six push notifications based on your prep time and advance
          warning preference:
        </p>
        <ul className="about-ul">
          <li>
            <strong>Get Ready warning</strong>: fires your chosen advance warning time before your
            get-ready time. For example: "Get ready in 30 minutes."
          </li>
          <li>
            <strong>Get Ready now</strong>: fires when you need ot het ready. For example: "Time to get ready!"
          </li>
          <li>
            <strong>Leave Home warning and Leave Home now</strong> fire if you are getting ready at a
            separate location (hotel, Trade's house, friend's place) and need to leave your home first.
          </li>
          <li>
            <strong>Leave warning</strong>: fires your advance warning time before you need to leave.
           For example: "Leave in 30 minutes."
          </li>
          <li>
            <strong>Leave now</strong>: fires at your exact leave time.
          </li>
        </ul>

        <p className="about-prose">
          Set your prep time and advance warning preference in Settings. If you add a gig after
          some notification times have already passed, the app fires those alerts straight away
          so you never miss one. Re-saving a gig after changing the call time automatically
          recalculates and resets all notifications.
        </p>

        <p className="about-prose">
          <strong>Going on a trip?</strong> If you have a trip logged that falls on a gig date, the
          app routes from your "Trip Home" address instead of your usual home. 
        </p>

        <p className="about-prose">
          <strong>Multiple gigs in one day?</strong> The today card can calculate venue-to-venue
          drive time for back-to-back shows, so you know exactly when to leave for the next gig!
        </p>

        <p className="about-prose">
          <strong>Away from home?</strong> On gig day, the app checks your GPS location. If you
          are more than .3 miles from home, it tells you how long it will take to get back
          before you need to leave.
        </p>

        <p className="about-prose">
          Notifications arrive on your device even when the app is closed. On iOS and Android, you need to
          install the app to your home screen first. On desktop Chrome they work directly from the browser. 
          But remember, you won't get any notifications until you opt in on the Settings page! <strong>Notifications are still a work in progress 
          and not fully operational.</strong>
        </p>
        <p className="about-prose text-muted" style={{ fontSize: 'var(--text-sm)', opacity: 0.7, marginBottom: 0 }}>
          You can turn off notifications any time in Settings. Nothing is sent until you opt in.
          See our <Link to="/privacy" className="link-primary">Privacy Policy</Link> for
          details on how notification data is handled.
        </p>
      </div>

      {/* REQUESTS INFO */}
      <div className="about-info-section">
        <h2>How booking requests work</h2>
        <p className="about-prose">
          Producers using the <strong>Producer Pocket Planner</strong> can search for performers by name
          and send show booking requests directly to your account. When a request arrives, you will
          see a badge on the Requests page in the bottom nav.
        </p>
        <ul className="about-ul">
          <li>
            <strong>Performance Requests</strong>: incoming show bookings from producers. Review
            the show details, pay, call time, and notes before accepting or declining.
          </li>
          <li>
            <strong>Management Requests</strong>: a manager asking to represent you! Approving
            lets them view your calendar and send gig management requests. You can share your
            contact info (legal name, travel preferences, promo photo links) at the same time.
          </li>
          <li>
            <strong>Manage a Gig</strong>: a manager asking to handle a specific booking on your
            behalf. The app shows the proposed fee and your projected take-home pay before you decide.
          </li>
        </ul>
        <p className="about-prose">
          The app checks for schedule conflicts and trip location mismatches automatically. If
          accepting a show would clash with an existing gig or fall on a trip day in a different
          city, a warning message appears before anything is saved.
        </p>
        <p className="about-prose text-muted" style={{ fontSize: 'var(--text-sm)', opacity: 0.7, marginBottom: 0 }}>
          The Producer Pocket Planner and Manager Pocket Planner are companion apps currently in
          development.
        </p>
      </div>


      {/* BETA NOTICE */}
      {/* .about-beta-section: pink-pale card */}
      <div className="about-beta-section">
        <h2>Hello!</h2>
        <p className="about-prose">
          Thanks for using {APP_NAME}! Here is what to expect:
        </p>
        <ul className="about-ul">
          <li>You may run into issues. Please report them using the link below.</li>
          <li>Features may change between updates.</li>
          <li>Your data is stored securely.</li>
          <li>The app works best in Chrome or Safari on a phone or desktop.</li>
          <li>You can install it to your home screen. On iOS, tap Share then "Add to Home Screen". On Android, use the browser install prompt.</li>
        </ul>
        <p className="about-prose-italic">
          Even a quick "this confused me" is super helpful. Thank you!
        </p>
      </div>


      {/* FEEDBACK */}
      <section className="about-section">
        <h2>Send feedback</h2>
        <p className="about-prose">
          Found an issue? Have a feature idea? Something felt confusing? Something felt homophobic? Get in touch and let me know.
        </p>
        <a
          href={CONTACT_SITE}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary about-feedback-link"
        >
          <i className="bi bi-envelope-heart-fill"></i>
          Contact via {CONTACT_LABEL}
        </a>
      </section>


      {/* ABOUT THE CREATOR */}
      <section className="about-section">
        <h2>Who made this?</h2>
        <p className="about-prose">
          {APP_NAME} was designed and built by <strong>King Perk</strong>, a performer and web
          developer who can slay the stage, but gets overwhelmed by keeping up with all his gig. This is a passion project built
          with love (and a lot of caffeine). It is free to use! I just want this to be an accessible resource for the community. 
        </p>
      </section>


      {/* FOOTER */}
      {/* .about-page-footer: border-top flex row with copyright + privacy link */}
      <div className="about-page-footer">
        <span className="about-page-footer-copy">
          &copy; {new Date().getFullYear()} {APP_NAME} &middot; v{APP_VERSION}
        </span>
        <Link to="/privacy" className="link-primary" style={{ fontSize: 'var(--text-sm)' }}>
          Privacy Policy
        </Link>
      </div>

    </div>
  )
}

export default About